import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import '../styles/Buttons.css';

interface BulkSpaceModalProps {
  isOpen: boolean;
  workspaceId: string;
  projectId: string;
  floorId: string;
  entityType: 'parking' | 'locker' | 'bicycle';
  spaceDefaults?: { width: number; height: number };
  layoutDimensions?: { width: number; height: number };
  existingSpaces: any[];
  onClose: () => void;
  onSave: (spaces: any[]) => Promise<void>;
}

interface BulkSpaceFormData {
  startNumber: string;
  endNumber: string;
  prefix: string;
  suffix: string;
  type: string;
  status: string;
  isPremium: boolean;
  autoPosition: boolean;
  startX: string;
  startY: string;
  spacing: string;
  orientation: 'horizontal' | 'vertical';
}

const BulkSpaceModal: React.FC<BulkSpaceModalProps> = ({
  isOpen,
  workspaceId,
  projectId,
  floorId,
  entityType,
  spaceDefaults = { width: 150, height: 75 },
  layoutDimensions = { width: 1000, height: 800 },
  existingSpaces,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState<BulkSpaceFormData>({
    startNumber: '1',
    endNumber: '10',
    prefix: '',
    suffix: '',
    type: '',
    status: 'AVAILABLE',
    isPremium: false,
    autoPosition: true,
    startX: '50',
    startY: '50',
    spacing: '20',
    orientation: 'horizontal'
  });

  const [types, setTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState('');
  const [previewSpaces, setPreviewSpaces] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchTypes();
    }
  }, [isOpen, entityType, projectId]);

  useEffect(() => {
    updatePreview();
  }, [formData.startNumber, formData.endNumber, formData.prefix, formData.suffix]);

  const fetchTypes = async () => {
    try {
      setLoadingTypes(true);

      if (entityType === 'parking') {
        const { parkingTypeService } = await import('../services/parkingService');
        const response = await parkingTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.parkingTypes || []);
        if (response.parkingTypes?.length > 0) {
          setFormData(prev => ({ ...prev, type: response.parkingTypes[0].name }));
        }
      } else if (entityType === 'locker') {
        const { lockerTypeService } = await import('../services/lockerService');
        const response = await lockerTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.lockerTypes || []);
        if (response.lockerTypes?.length > 0) {
          setFormData(prev => ({ ...prev, type: response.lockerTypes[0].name }));
        }
      } else if (entityType === 'bicycle') {
        const { bicycleTypeService } = await import('../services/bicycleService');
        const response = await bicycleTypeService.getAll(workspaceId, { projectId, limit: 1000 });
        setTypes(response.bicycleTypes || []);
        if (response.bicycleTypes?.length > 0) {
          setFormData(prev => ({ ...prev, type: response.bicycleTypes[0].name }));
        }
      }
    } catch (err: any) {
      console.error('Error fetching types:', err);
      setError('Failed to load types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const updatePreview = () => {
    const start = parseInt(formData.startNumber) || 1;
    const end = parseInt(formData.endNumber) || 1;

    if (start > end) {
      setPreviewSpaces([]);
      return;
    }

    const count = end - start + 1;
    if (count > 100) {
      setPreviewSpaces([`Too many spaces (${count}). Maximum 100 allowed.`]);
      return;
    }

    const spaces: string[] = [];
    for (let i = start; i <= end; i++) {
      spaces.push(`${formData.prefix}${i}${formData.suffix}`);
    }
    setPreviewSpaces(spaces);
  };

  const calculateAutoPositions = (count: number) => {
    const startX = parseInt(formData.startX) || 50;
    const startY = parseInt(formData.startY) || 50;
    const spacing = parseInt(formData.spacing) || 20;
    const { width, height } = spaceDefaults;

    const positions: Array<{ left: number; top: number }> = [];

    if (formData.orientation === 'horizontal') {
      // Arrange horizontally with wrapping
      const maxPerRow = Math.floor((layoutDimensions.width - startX) / (width + spacing));

      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / maxPerRow);
        const col = i % maxPerRow;

        positions.push({
          left: startX + col * (width + spacing),
          top: startY + row * (height + spacing)
        });
      }
    } else {
      // Arrange vertically with wrapping
      const maxPerCol = Math.floor((layoutDimensions.height - startY) / (height + spacing));

      for (let i = 0; i < count; i++) {
        const col = Math.floor(i / maxPerCol);
        const row = i % maxPerCol;

        positions.push({
          left: startX + col * (width + spacing),
          top: startY + row * (height + spacing)
        });
      }
    }

    return positions;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.type) {
      setError('Please select a type');
      return;
    }

    const start = parseInt(formData.startNumber) || 1;
    const end = parseInt(formData.endNumber) || 1;

    if (start > end) {
      setError('Start number must be less than or equal to end number');
      return;
    }

    const count = end - start + 1;
    if (count > 100) {
      setError('Maximum 100 spaces can be created at once');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const positions = formData.autoPosition ? calculateAutoPositions(count) : [];
      const spaces: any[] = [];

      for (let i = start; i <= end; i++) {
        const number = `${formData.prefix}${i}${formData.suffix}`;

        // Check if space number already exists
        const exists = existingSpaces.some(s => s.number === number);
        if (exists) {
          throw new Error(`Space number "${number}" already exists`);
        }

        const spaceData: any = {
          number,
          type: formData.type,
          status: formData.status,
          isPremium: formData.isPremium,
          floorId,
          projectId,
          workspaceId
        };

        // Add position if auto-position is enabled
        if (formData.autoPosition && positions[i - start]) {
          const pos = positions[i - start];
          spaceData.spaceObject = {
            type: 'rect',
            left: pos.left,
            top: pos.top,
            width: spaceDefaults.width,
            height: spaceDefaults.height,
            fill: 'transparent',
            stroke: '#000000',
            strokeWidth: 2,
            id: '', // Will be set after creation
            number: number,
            spaceType: formData.type,
            isPremium: formData.isPremium,
            status: formData.status
          };
        }

        spaces.push(spaceData);
      }

      await onSave(spaces);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create spaces');
      console.error('Error creating bulk spaces:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Bulk Add {entityLabel} Spaces</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#FEE2E2',
                color: '#991B1B',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}

            {/* Space Numbering */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Space Numbering
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">
                    Start Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="startNumber"
                    className="form-input"
                    value={formData.startNumber}
                    onChange={handleInputChange}
                    required
                    min="1"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    End Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="endNumber"
                    className="form-input"
                    value={formData.endNumber}
                    onChange={handleInputChange}
                    required
                    min="1"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Prefix (Optional)</label>
                  <input
                    type="text"
                    name="prefix"
                    className="form-input"
                    value={formData.prefix}
                    onChange={handleInputChange}
                    placeholder="e.g., P, A, B"
                    maxLength={10}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Suffix (Optional)</label>
                  <input
                    type="text"
                    name="suffix"
                    className="form-input"
                    value={formData.suffix}
                    onChange={handleInputChange}
                    placeholder="e.g., -A, -B"
                    maxLength={10}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Preview */}
              {previewSpaces.length > 0 && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    Preview ({previewSpaces.length} spaces):
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', maxHeight: '100px', overflow: 'auto' }}>
                    {previewSpaces.join(', ')}
                  </div>
                </div>
              )}
            </div>

            {/* Space Properties */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Space Properties
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">
                    Type <span style={{ color: 'red' }}>*</span>
                  </label>
                  {loadingTypes ? (
                    <div style={{ padding: '10px', textAlign: 'center' }}>
                      <div className="builders-loading-spinner" style={{ width: '20px', height: '20px' }} />
                    </div>
                  ) : (
                    <select
                      name="type"
                      className="form-input"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      {types.length === 0 ? (
                        <option value="">No types available</option>
                      ) : (
                        types.map((type) => (
                          <option key={type._id} value={type.name}>
                            {type.name}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Status <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="status"
                    className="form-input"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  >
                    <option value="AVAILABLE">Available</option>
                    <option value="MARKETING HOLD">Marketing Hold</option>
                    <option value="SALES HOLD">Sales Hold</option>
                    <option value="CUSTOMER SELECTION">Customer Selection</option>
                    <option value="CUSTOMER SUBMITTED">Customer Submitted</option>
                    <option value="SOLD">Sold</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="isPremium"
                    checked={formData.isPremium}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <span className="form-label" style={{ margin: 0 }}>Mark as Premium</span>
                </label>
              </div>
            </div>

            {/* Auto-Positioning */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Layout Positioning
              </h3>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="autoPosition"
                    checked={formData.autoPosition}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  <span className="form-label" style={{ margin: 0 }}>
                    Automatically position spaces on floor layout
                  </span>
                </label>
              </div>

              {formData.autoPosition && (
                <div style={{ marginTop: '12px', padding: '16px', backgroundColor: '#F3F4F6', borderRadius: '6px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Start X Position (px)</label>
                      <input
                        type="number"
                        name="startX"
                        className="form-input"
                        value={formData.startX}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Start Y Position (px)</label>
                      <input
                        type="number"
                        name="startY"
                        className="form-input"
                        value={formData.startY}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Spacing (px)</label>
                      <input
                        type="number"
                        name="spacing"
                        className="form-input"
                        value={formData.spacing}
                        onChange={handleInputChange}
                        min="0"
                        disabled={loading}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Orientation</label>
                      <select
                        name="orientation"
                        className="form-input"
                        value={formData.orientation}
                        onChange={handleInputChange}
                        disabled={loading}
                      >
                        <option value="horizontal">Horizontal (Left to Right)</option>
                        <option value="vertical">Vertical (Top to Bottom)</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>
                    Spaces will be arranged automatically with the specified spacing.
                    They will wrap to the next row/column if they exceed the layout boundaries.
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={loading || loadingTypes || types.length === 0 || previewSpaces.length === 0}
            >
              {loading ? 'Creating...' : `Create ${previewSpaces.length} Spaces`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkSpaceModal;
